import { useNavigate } from "react-router-dom";
import { Truck, Sparkles, Clock, Leaf, GlassWater, PartyPopper } from "lucide-react";
import Reveal from "../components/Reveal";
import SectionHeader from "../components/SectionHeader";
import CTABanner from "../components/CTABanner";
import { IconStar, IconArrow, IconQuote } from "../components/icons";
import { TESTIMONIALS } from "../data";
import heroHome from "../assets/images/hero/hero-home.png";
import butterflyLogo from "../assets/images/logo/butterfly-logo.svg";
import whatWeDoCocktailClass from "../assets/images/what-we-do/cocktail-class.jpeg";
import whatWeDoCocktailService from "../assets/images/what-we-do/cocktail-service.JPG";

export default function Home() {
  const navigate = useNavigate();
  return (
    <main>
      {/* ── Hero ── */}
      <section className="sb-noise" style={{
        position: "relative", minHeight: "100vh", display: "flex", alignItems: "center",
        justifyContent: "center", textAlign: "center", overflow: "hidden",
        backgroundImage: `url(${heroHome})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}>
        {/* Dark overlay for content legibility */}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(170deg, rgba(10,6,20,0.72) 0%, rgba(58,24,68,0.65) 40%, rgba(80,30,60,0.60) 100%)",
          zIndex: 0,
        }} />
        {/* Decorative orbs */}
        <div style={{ position: "absolute", top: "10%", left: "8%", width: "340px", height: "340px", borderRadius: "50%", background: "radial-gradient(circle, rgba(212,168,83,0.1), transparent 70%)", animation: "sb-float 8s ease-in-out infinite", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "10%", right: "10%", width: "280px", height: "280px", borderRadius: "50%", background: "radial-gradient(circle, rgba(199,125,138,0.08), transparent 70%)", animation: "sb-float 10s ease-in-out infinite 1s", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "600px", height: "600px", borderRadius: "50%", background: "radial-gradient(circle, rgba(212,168,83,0.05), transparent 60%)", pointerEvents: "none" }} />

        <div className="sb-container" style={{ position: "relative", zIndex: 2, paddingTop: "var(--nav-h)" }}>
          <div style={{ animation: "sb-fadeUp 1s ease 0.2s both" }}>
            <img
              src={butterflyLogo}
              alt="Silly Butterfly logo"
              style={{
                display: "inline-block", width: "3.2rem", height: "3.2rem",
                marginBottom: "20px", objectFit: "contain",
                filter: "drop-shadow(0 0 20px rgba(212,168,83,0.3))",
                animation: "sb-float 4s ease-in-out infinite",
              }}
            />
          </div>
          <div style={{ animation: "sb-fadeUp 1s ease 0.4s both" }}>
            <span style={{
              display: "inline-block", fontFamily: "var(--font-body)", fontWeight: 600,
              fontSize: "0.82rem", letterSpacing: "0.18em", textTransform: "uppercase",
              color: "var(--gold)", marginBottom: "18px",
              padding: "6px 18px", borderRadius: "99px",
              background: "rgba(212,168,83,0.1)", border: "1px solid rgba(212,168,83,0.2)",
            }}>
              Cocktail Making Classes & Cocktail Services
            </span>
          </div>
          <div style={{ animation: "sb-fadeUp 1s ease 0.6s both" }}>
            <h1 style={{
              color: "var(--white)", maxWidth: "800px", margin: "0 auto 20px",
              textShadow: "0 4px 40px rgba(0,0,0,0.3)",
            }}>
              Cocktail Experiences <br />
              <span style={{
                background: "linear-gradient(135deg, var(--gold), var(--rose))",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>
                Worth Celebrating
              </span>
            </h1>
          </div>
          <div style={{ animation: "sb-fadeUp 1s ease 0.8s both" }}>
            <p style={{
              color: "rgba(255,255,255,0.7)", maxWidth: "560px", margin: "0 auto 36px",
              fontSize: "clamp(1rem, 1.3vw, 1.15rem)", lineHeight: 1.7,
            }}>
              Whether you're planning a hen party, birthday celebration, corporate social, or private event, we bring the experience to you. Cocktail making classes and cocktail services for your guests.
            </p>
          </div>
          <div style={{ animation: "sb-fadeUp 1s ease 1s both", display: "flex", gap: "14px", justifyContent: "center", flexWrap: "wrap" }}>
            <button className="sb-btn sb-btn-primary" onClick={() => { navigate("/experience"); window.scrollTo({ top: 0 }); }}>
              Explore Packages <IconArrow />
            </button>
            <button className="sb-btn sb-btn-secondary" onClick={() => { navigate("/contact"); window.scrollTo({ top: 0 }); }}>
              Get a Quote
            </button>
          </div>
          {/* Trust strip */}
          <div style={{
            animation: "sb-fadeUp 1s ease 1.2s both",
            marginTop: "48px", display: "flex", alignItems: "center", justifyContent: "center",
            gap: "24px", flexWrap: "wrap", fontSize: "0.82rem", color: "rgba(255,255,255,0.45)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {[...Array(5)].map((_, i) => <span key={i} style={{ color: "var(--gold)" }}><IconStar /></span>)}
            </div>
            <span style={{ opacity: 0.3 }}>|</span>
            <span>Mobile Service</span>
          </div>
        </div>

        {/* Bottom fade */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "120px", background: "linear-gradient(to top, var(--blush-tint), transparent)", zIndex: 2 }} />
      </section>

      {/* ── What We Do ── */}
      <section className="sb-section">
        <div className="sb-container">
          <SectionHeader
            label="What We Do"
            title="Two Ways to Experience Cocktails"
            subtitle="Whether your guests want to get hands-on or simply sip back and enjoy."
          />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))", gap: "28px" }}>
            {[
              {
                title: "Cocktail Making Class",
                desc: "Experience the art of mixology. Our interactive classes teach cocktail-making skills in a fun, social, and lively atmosphere. Perfect for breaking the ice, bonding with friends, or celebrating in style.",
                img: whatWeDoCocktailClass,
              },
              {
                title: "Cocktail Service",
                desc: "Sit back, relax, and let our professional bartenders do the shaking while you celebrate with elegance. Perfect for those who want to sip their cocktails, socialize and stay in the moment.",
                img: whatWeDoCocktailService,
              },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div style={{
                  background: "var(--white)", borderRadius: "var(--radius-lg)", overflow: "hidden",
                  boxShadow: "var(--shadow-md)", border: "1px solid rgba(212,168,83,0.08)",
                  transition: "transform 0.4s ease, box-shadow 0.4s ease", cursor: "default",
                  height: "100%", display: "flex", flexDirection: "column",
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "var(--shadow-lg)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "var(--shadow-md)"; }}
                >
                  <div style={{ width: "100%", height: "280px", overflow: "hidden" }}>
                    <img
                      src={s.img}
                      alt={s.title}
                      loading="lazy"
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.5s ease" }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.04)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
                    />
                  </div>
                  <div style={{ padding: "clamp(24px, 4vw, 36px)", flex: 1 }}>
                    <h3 style={{ marginBottom: "12px", color: "var(--plum)" }}>{s.title}</h3>
                    <p style={{ color: "var(--text-secondary)" }}>{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div style={{ textAlign: "center", marginTop: "36px" }}>
              <button className="sb-btn sb-btn-dark" onClick={() => { navigate("/experience"); window.scrollTo({ top: 0 }); }}>
                View All Packages <IconArrow />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── How It Works / Good to Know ── */}
      <section className="sb-section" style={{ background: "var(--blush-glow)" }}>
        <div className="sb-container">

          {/* How It Works */}
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: "clamp(48px, 8vw, 80px)" }}>
              <span style={{
                display: "inline-block", fontFamily: "var(--font-body)", fontWeight: 600,
                fontSize: "0.72rem", letterSpacing: "0.18em", textTransform: "uppercase",
                color: "var(--gold)", marginBottom: "14px",
              }}>How It Works</span>
              <h2 style={{ color: "var(--plum)", marginBottom: "16px" }}>From Enquiry to Celebration</h2>
              <p style={{ color: "var(--text-secondary)", maxWidth: "560px", margin: "0 auto" }}>
                No matter the occasion, we bring the cocktails and the experience.
              </p>
            </div>
          </Reveal>

          {/* ── 2-column: How It Works (left) + Good to Know (right) ── */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 480px), 1fr))",
            gap: "clamp(40px, 6vw, 72px)",
            alignItems: "start",
          }}>

            {/* LEFT — Your Steps */}
            <div>
              <Reveal>
                <span style={{
                  display: "inline-block", fontFamily: "var(--font-body)", fontWeight: 600,
                  fontSize: "0.72rem", letterSpacing: "0.18em", textTransform: "uppercase",
                  color: "var(--gold)", marginBottom: "clamp(24px, 4vw, 36px)",
                }}>Your Steps</span>
              </Reveal>

              <div style={{ position: "relative" }}>
                {/* Vertical connector line */}
                <div style={{
                  position: "absolute", left: "clamp(17px, 2.5vw, 21px)", top: "36px",
                  bottom: "36px", width: "1px",
                  background: "linear-gradient(180deg, rgba(212,168,83,0.5), rgba(212,168,83,0.05))",
                }} />

                {[
                  { step: "01", title: "Choose Your Experience", body: "Select your preferred cocktail experience and package to suit your event." },
                  { step: "02", title: "Plan & Confirm", body: "Share your event details and we'll check availability, provide a tailored quote, and organise everything with you in advance." },
                  { step: "03", title: "Enjoy the Experience", body: "We arrive early to set up at your chosen location, so you can relax while our mixologists serve or guide you throughout." },
                ].map((item, i) => (
                  <Reveal key={i} delay={i * 0.08}>
                    <div style={{
                      display: "flex", alignItems: "flex-start", gap: "clamp(16px, 2.5vw, 24px)",
                      marginBottom: i < 2 ? "clamp(24px, 4vw, 32px)" : 0,
                      position: "relative",
                    }}>
                      <div style={{
                        flexShrink: 0,
                        width: "clamp(36px, 4.5vw, 44px)", height: "clamp(36px, 4.5vw, 44px)",
                        borderRadius: "50%",
                        background: i === 0 ? "linear-gradient(135deg, var(--gold), #E0B95C)" : "rgba(212,168,83,0.08)",
                        border: i === 0 ? "none" : "1.5px solid rgba(212,168,83,0.3)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        zIndex: 1,
                      }}>
                        <span style={{
                          fontFamily: "var(--font-display)", fontWeight: 700,
                          fontSize: "0.78rem",
                          color: i === 0 ? "var(--plum)" : "var(--gold)",
                          lineHeight: 1,
                        }}>{item.step}</span>
                      </div>
                      <div style={{ flex: 1, paddingTop: "8px" }}>
                        <h4 style={{ color: "var(--plum)", marginBottom: "4px", fontSize: "clamp(0.95rem, 1.2vw, 1.05rem)" }}>{item.title}</h4>
                        <p style={{ color: "var(--text-secondary)", fontSize: "clamp(0.85rem, 0.95vw, 0.92rem)", lineHeight: 1.65, margin: 0 }}>{item.body}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            {/* RIGHT — Good to Know */}
            <div>
              <Reveal>
                <span style={{
                  display: "inline-block", fontFamily: "var(--font-body)", fontWeight: 600,
                  fontSize: "0.72rem", letterSpacing: "0.18em", textTransform: "uppercase",
                  color: "var(--gold)", marginBottom: "clamp(24px, 4vw, 36px)",
                }}>Good to Know</span>
              </Reveal>

              <Reveal>
                <div style={{
                  background: "linear-gradient(135deg, var(--plum) 0%, var(--plum-mid) 100%)",
                  borderRadius: "var(--radius-lg)",
                  boxShadow: "var(--shadow-md)",
                  border: "1px solid rgba(212,168,83,0.12)",
                  overflow: "hidden",
                }}>
                  {[
                    { icon: <Truck size={17} strokeWidth={1.80} />, body: "We provide a fully mobile cocktail service at your location" },
                    { icon: <Sparkles size={17} strokeWidth={1.80} />, body: "We offer bespoke cocktail packages & services upon request" },
                    { icon: <Clock size={17} strokeWidth={1.80} />, body: "Your experience will last approximately 2 hours, depending on the group size and package selected" },
                    { icon: <Leaf size={17} strokeWidth={1.80} />, body: "Mocktail options available to suit your guests' preferences" },
                    { icon: <GlassWater size={17} strokeWidth={1.80} />, body: "Bar equipment, ingredients, glassware, and garnishes included" },
                    { icon: <PartyPopper size={17} strokeWidth={1.80} />, body: "Made for every occasion. From hen parties and birthdays to corporate events and private celebrations." },
                  ].map((item, i, arr) => (
                    <div key={i} style={{
                      display: "flex", gap: "16px", alignItems: "center",
                      padding: "18px 24px",
                      borderBottom: i < arr.length - 1 ? "1px solid rgba(212,168,83,0.09)" : "none",
                    }}>
                      <div style={{
                        flexShrink: 0, width: "38px", height: "38px", borderRadius: "var(--radius-sm)",
                        background: "rgba(212,168,83,0.10)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "var(--gold)",
                      }}>{item.icon}</div>
                      <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.875rem", lineHeight: 1.6, margin: 0 }}>{item.body}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

          </div>

        </div>
      </section>

      {/* ── Quick Testimonials ── */}
      <section className="sb-section">
        <div className="sb-container">
          <SectionHeader
            label="Loved by Our Guests"
            title="Don't Just Take Our Word for It"
            subtitle="See what our amazing clients have to say about their Silly Butterfly experience."
          />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))", gap: "24px" }}>
            {TESTIMONIALS.slice(0, 3).map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div style={{
                  background: "var(--white)", borderRadius: "var(--radius-md)", padding: "28px",
                  boxShadow: "var(--shadow-sm)", border: "1px solid rgba(212,168,83,0.08)",
                  position: "relative", height: "100%",
                }}>
                  <span style={{ position: "absolute", top: "16px", right: "20px", color: "var(--gold)" }}><IconQuote /></span>
                  <div style={{ display: "flex", gap: "2px", marginBottom: "14px" }}>
                    {[...Array(t.rating)].map((_, j) => <span key={j} style={{ color: "var(--gold)" }}><IconStar /></span>)}
                  </div>
                  <p style={{ color: "var(--text-secondary)", fontSize: "0.92rem", marginBottom: "16px", fontStyle: "italic", lineHeight: 1.7 }}>
                    "{t.text}"
                  </p>
                  <span style={{ fontWeight: 600, color: "var(--plum)", fontSize: "0.9rem" }}>— {t.name}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <div style={{ textAlign: "center", marginTop: "32px" }}>
              <button className="sb-btn sb-btn-outline" onClick={() => { navigate("/gallery"); window.scrollTo({ top: 0 }); }}>
                See More Reviews <IconArrow />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <CTABanner />
    </main>
  );
}
